import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faBrain,
  faChartLine,
  faFilePdf,
  faGear,
  faLightbulb,
  faPaperPlane,
  faRotate,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import { CanView } from "../../../components/CanView/CanView"
import { Permissions } from "../../../Enum/PermissionsType"
import { useAiIntegration, defaultAiIntegrationSettings } from "../../../hooks/AiIntegration/useAiIntegration"
import { IAiIntegrationSettings } from "../../../Objects/AiIntegration/IAiIntegrationSettings"
import { IAiQuestionResponse } from "../../../Objects/AiIntegration/IAiQuestionResponse"
import { AiIntegrationSettingsPopup } from "./components/AiIntegrationSettingsPopup"
import { useQuickActionPopup } from "../../../hooks/useQuickActionPopup"

const suggestedQuestionKeys = [
  "label_ai_integration_suggestion_investment",
  "label_ai_integration_suggestion_menu",
  "label_ai_integration_suggestion_delivery",
  "label_ai_integration_suggestion_growth",
  "label_ai_integration_suggestion_metrics_pdf",
  "label_ai_integration_suggestion_products_pdf",
  "label_ai_integration_suggestion_products_analysis",
  "label_ai_integration_suggestion_customers",
  "label_ai_integration_suggestion_promotions",
  "label_ai_integration_suggestion_margin",
  "label_ai_integration_suggestion_schedule",
  "label_ai_integration_suggestion_stock",
]

export const AiIntegrationPage: React.FC = () => {
  const { t } = useTranslation(["pages", "helpPopup"])

  const { getSettings, saveSettings, ask } = useAiIntegration()

  const [settings, setSettings] = useState<IAiIntegrationSettings>(defaultAiIntegrationSettings)

  const [question, setQuestion] = useState("")

  const [answer, setAnswer] = useState<IAiQuestionResponse | null>(null)

  const [isSettingsPopupOpen, setIsSettingsPopupOpen] = useState(false)

  const [isAsking, setIsAsking] = useState(false)

  const canAskAi =
    settings.isEnabled &&
    settings.endpointUrl.trim().length > 0 &&
    settings.apiKey.trim().length > 0 &&
    settings.model.trim().length > 0

  useQuickActionPopup("ai-settings", () => setIsSettingsPopupOpen(true))

  const load = async () => setSettings(await getSettings())

  useEffect(() => {
    void load()
  }, [])

  const handleSave = async () => {
    setSettings(await saveSettings(settings))
    setIsSettingsPopupOpen(false)
  }

  const handleAsk = async () => {
    const trimmed = question.trim()

    if (!trimmed || isAsking) {
      return
    }

    setIsAsking(true)

    try {
      setAnswer(await ask(trimmed))
    } finally {
      setIsAsking(false)
    }
  }

  const handlePrintAnswer = () => {
    if (!answer) {
      return
    }

    const printWindow = window.open("", "_blank", "width=900,height=700")

    if (!printWindow) {
      return
    }

    const documentTitle = t("label_ai_integration_print_title")

    const meta = t("label_ai_integration_answer_meta", {
      provider: answer.provider,
      model: answer.model,
      months: answer.contextMonths,
    })

    printWindow.document.title = documentTitle
    printWindow.document.body.innerHTML = ""

    const style = printWindow.document.createElement("style")

    style.textContent = `
      body {
        color: #0f172a;
        font-family: Arial, sans-serif;
        line-height: 1.55;
        margin: 32px;
      }

      h1 {
        font-size: 22px;
        margin: 0 0 8px;
      }

      .meta {
        border-bottom: 1px solid #cbd5e1;
        color: #475569;
        font-size: 12px;
        font-weight: 700;
        margin-bottom: 24px;
        padding-bottom: 12px;
      }

      .answer {
        white-space: pre-wrap;
        font-size: 14px;
      }
    `

    const title = printWindow.document.createElement("h1")

    title.textContent = documentTitle

    const metaElement = printWindow.document.createElement("div")

    metaElement.className = "meta"

    metaElement.textContent = meta

    const answerElement = printWindow.document.createElement("div")

    answerElement.className = "answer"

    answerElement.textContent = answer.answer

    printWindow.document.head.appendChild(style)

    printWindow.document.body.append(title, metaElement, answerElement)

    printWindow.document.close()

    printWindow.focus()

    printWindow.print()
  }

  return (
    <div className="h-[calc(100%-25px)] w-full max-w-full overflow-x-hidden overflow-y-auto p-2 sm:p-4 md:p-2">
      <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
        <div className="w-full max-w-full overflow-x-hidden">
          <div className="mb-3 mt-3 flex flex-row items-start justify-between p-2">
            <h2 className="flex items-center gap-2 text-lg font-bold text-slate-800 sm:text-xl">
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-sky-100 text-sky-700">
                <FontAwesomeIcon icon={faBrain} className="text-sm" />
              </span>

              {t("label_ai_integration_title")}
            </h2>

            <PopupHelpButton
              variant="global"
              helpKey="pages.ai-integration"
              title={t("label_ai_integration_title")}
              description={t("label_ai_integration_subtitle")}
              bullets={t("helpPopup:global.aiIntegration.bullets", { returnObjects: true }) as string[]}
              steps={t("helpPopup:global.aiIntegration.steps", { returnObjects: true }) as string[]}
            />
          </div>

          <Animate type={AnimationTypes.fadeInTraslate} duration="1.3s">
            <div className="mb-4 grid gap-2 sm:flex sm:flex-wrap">
              <CanView permission={Permissions.CanEditeCompany}>
                <button
                  type="button"
                  onClick={() => setIsSettingsPopupOpen(true)}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl border-2 border-sky-300 bg-sky-50 px-4 py-2 text-sm font-bold text-sky-700 shadow-sm transition hover:bg-sky-100 sm:w-auto"
                  data-interaction-description-key="interactionDescriptions:pages.myRestaurant.aiIntegration.aiIntegrationPage.button.setIsSettingsPopupOpenTrue"
                >
                  <FontAwesomeIcon icon={faGear} />
                  {t("label_ai_integration_open_settings")}
                </button>
              </CanView>

              <button
                type="button"
                onClick={() => void load()}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 sm:w-auto"
                data-interaction-description-key="interactionDescriptions:pages.myRestaurant.aiIntegration.aiIntegrationPage.button.voidLoad"
              >
                <FontAwesomeIcon icon={faRotate} />
                {t("label_ai_integration_refresh")}
              </button>
            </div>
          </Animate>

          {!canAskAi && (
            <Animate type={AnimationTypes.fadeInTraslate} duration="1.4s">
              <div className="mb-4 rounded-lg border-2 border-amber-200 bg-amber-50 p-4 text-amber-950">
                <div className="flex flex-col gap-3 text-center sm:flex-row sm:items-start sm:text-left">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center self-center rounded-lg bg-white text-amber-700 sm:self-start">
                    <FontAwesomeIcon icon={faTriangleExclamation} />
                  </span>

                  <div className="min-w-0">
                    <strong className="block text-sm">{t("label_ai_integration_disabled_title")}</strong>
                    <p className="mt-1 text-xs text-justify font-medium leading-relaxed">
                      {t("label_ai_integration_disabled_description")}
                    </p>
                  </div>
                </div>
              </div>
            </Animate>
          )}

          {canAskAi && (
            <div className="grid w-full max-w-full gap-3 lg:grid-cols-[minmax(0,360px)_minmax(0,1fr)]">
              <Animate type={AnimationTypes.fadeInTraslate} duration="1.4s">
                <section className="min-w-0 rounded-lg border-2 border-slate-200 bg-white p-3 shadow-sm">
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faLightbulb} className="text-amber-500" />
                    <strong className="text-sm text-slate-900">{t("label_ai_integration_suggestions_title")}</strong>
                  </div>

                  <p className="mt-1 text-xs leading-relaxed text-slate-500">
                    {t("label_ai_integration_suggestions_description")}
                  </p>

                  <div className="mt-3 grid gap-2">
                    {suggestedQuestionKeys.map((key, index) => (
                      <Animate
                        key={key}
                        type={AnimationTypes.fadeInTraslate}
                        duration="1.2s"
                        delay={`${index * 0.04}s`}
                      >
                        <button
                          type="button"
                          onClick={() => setQuestion(t(key))}
                          className="min-w-0 rounded-lg border-2 border-slate-100 bg-slate-50 p-3 text-left text-xs font-semibold leading-relaxed text-slate-700 transition hover:border-sky-200 hover:bg-sky-50 hover:text-sky-800"
                          data-interaction-description-key="interactionDescriptions:pages.myRestaurant.aiIntegration.aiIntegrationPage.button.setQuestionTKey"
                        >
                          {t(key)}
                        </button>
                      </Animate>
                    ))}
                  </div>
                </section>
              </Animate>

              <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                <section className="min-w-0 rounded-lg border-2 border-slate-200 bg-slate-50 p-3 shadow-sm">
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faChartLine} className="text-sky-600" />
                    <strong className="text-sm text-slate-900">{t("label_ai_integration_question_title")}</strong>
                  </div>

                  <p className="mt-1 text-xs leading-relaxed text-slate-500">
                    {t("label_ai_integration_question_description", { months: settings.monthsToAnalyze })}
                  </p>

                  <textarea
                    value={question}
                    onChange={(event) => setQuestion(event.target.value)}
                    maxLength={1200}
                    placeholder={t("label_ai_integration_question_placeholder")}
                    className="mt-3 min-h-[118px] w-full max-w-full resize-none rounded-lg border-2 border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-800 outline-none transition focus:border-sky-400"
                    data-interaction-description-key="interactionDescriptions:pages.myRestaurant.aiIntegration.aiIntegrationPage.input.aiIntegrationQuestionPlaceholder"
                  />

                  <div className="mt-3 flex justify-end">
                    <button
                      type="button"
                      onClick={() => void handleAsk()}
                      disabled={!question.trim() || isAsking || !canAskAi}
                      className="inline-flex min-h-[42px] w-full items-center justify-center gap-2 rounded-lg border-2 border-sky-500 bg-sky-600 px-4 py-2 text-sm font-black text-white shadow-sm transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:border-slate-300 disabled:bg-slate-300 sm:w-auto"
                      data-interaction-description-key="interactionDescriptions:pages.myRestaurant.aiIntegration.aiIntegrationPage.button.voidHandleAsk"
                    >
                      <FontAwesomeIcon icon={faPaperPlane} />
                      {isAsking ? t("label_ai_integration_asking") : t("label_ai_integration_ask")}
                    </button>
                  </div>

                  <div className="mt-4 min-h-[220px] rounded-lg border-2 border-white bg-white p-4">
                    {!answer && (
                      <div className="flex h-full min-h-[180px] items-center justify-center text-center text-xs text-slate-500">
                        {t("label_ai_integration_empty_answer")}
                      </div>
                    )}

                    {answer && (
                      <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
                        <div className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700">
                          <div className="mb-3 flex flex-col gap-2 rounded-lg border border-sky-100 bg-sky-50 p-2 text-[11px] font-bold text-sky-700 sm:flex-row sm:items-center sm:justify-between">
                            <span>
                              {t("label_ai_integration_answer_meta", {
                                provider: answer.provider,
                                model: answer.model,
                                months: answer.contextMonths,
                              })}
                            </span>

                            <button
                              type="button"
                              onClick={handlePrintAnswer}
                              className="inline-flex items-center justify-center gap-2 rounded-md border border-sky-200 bg-white px-3 py-1.5 text-[11px] font-black text-sky-700 shadow-sm transition hover:bg-sky-100"
                              data-interaction-description-key="interactionDescriptions:pages.myRestaurant.aiIntegration.aiIntegrationPage.button.handlePrintAnswer"
                            >
                              <FontAwesomeIcon icon={faFilePdf} />
                              {t("label_ai_integration_print_answer")}
                            </button>
                          </div>

                          {answer.answer}
                        </div>
                      </Animate>
                    )}
                  </div>
                </section>
              </Animate>
            </div>
          )}
        </div>
      </Animate>

      <AiIntegrationSettingsPopup
        isOpen={isSettingsPopupOpen}
        settings={settings}
        onChange={setSettings}
        onClose={() => setIsSettingsPopupOpen(false)}
        onSave={() => void handleSave()}
      />
    </div>
  )
}
